/**
 * useRoyaleSocket
 *
 * Façade the Puzzle Royale UI uses to drive the lobby/session, mirroring
 * `useFunChallengesBattleSocket` for the 1v1 modes. It wraps the shared
 * low-level `useFunChallengesSocket` envelope sender, always tagging the
 * royale gameType so inbound messages route to funChallengesRoyaleStore (see
 * royaleSocketRouting + the gameType branches in useFunChallengesSocket).
 *
 * Optimistic lobby: on createLobby we seed the royale store's lobby with the
 * host's chosen settings immediately (the server's fun_room_created ACK only
 * carries {roomId, creatorId}, so the settings must come from here). The
 * server then drives the roster via fun_room_update and the session via
 * fun_session_start.
 */

import {useCallback, useMemo} from 'react';

import useLoggedInUserStore from '@/Store/loggedInUserstore';
import useFunChallengesRoyaleStore from '@/Store/funChallengesRoyaleStore';
import {BattleType} from '@/Screens/FunChallenges/types';
import type {RoyaleSettings} from '@/Screens/FunChallenges/royaleTypes';
import {useFunChallengesSocket} from './useFunChallengesSocket';

export interface CreateRoyaleArgs {
  hostIsPlayer: boolean;
  settings: RoyaleSettings;
  creatorName: string;
  creatorAvatarUrl?: string;
}

export interface JoinRoyaleArgs {
  asSpectator?: boolean;
  username?: string;
  avatarUrl?: string;
}

export interface RoyaleSocketApi {
  ready: boolean;
  /** True once the socket has authenticated — gate auto-joins on this. */
  authed: boolean;
  createLobby: (args: CreateRoyaleArgs) => void;
  joinLobby: (shareCodeOrRoomId: string, args?: JoinRoyaleArgs) => void;
  leaveLobby: (roomId: string) => void;
  startRace: (
    roomId: string,
    args?: {hostIsPlayer?: boolean; hostWatching?: boolean},
  ) => void;
  /** Ask the server whether THIS user has an active session and, if so, replay
   *  it (fun_session_rejoin). Used to recover a client stuck in the waiting
   *  room that missed the one-shot fun_session_start broadcast — the server
   *  keys the lookup on userId, so no room/session id is needed. Tagged with
   *  the rejoin-probe requestId so a NO_ACTIVE_SESSION reply is silent. */
  probeSession: () => void;
  /** Host toggles play-vs-spectate in the waiting room; server re-broadcasts the
   *  roster so OTHER players see the change before start. */
  setHostRole: (roomId: string, hostIsPlayer: boolean) => void;
  sendMove: (sessionId: string, correct: 0 | 1, square: string) => void;
  forfeit: (sessionId: string) => void;
  /** Host rematch: server spins up a fresh lobby (same settings) off the ended
   *  session and pushes its code to everyone so they all auto-join. */
  rematch: (endedSessionId: string) => void;
  /** Spectate a live race by share code / roomId; server replies with
   *  fun_spectate_start (a read-only session bootstrap). Pass name/avatar so
   *  the spectator roster shows a real name (#1). */
  spectate: (shareCodeOrRoomId: string, args?: JoinRoyaleArgs) => void;
  /** Stream one ply of my board to spectators (FEN + from/to + orientation +
   *  kind). Fire-and-forget; the server fans it out to the room's spectators. */
  spectateMove: (
    sessionId: string,
    fen: string,
    from: string,
    to: string,
    orientation: 'white' | 'black',
    kind: 'setup' | 'player' | 'reply' | 'wrong',
  ) => void;
}

const ROYALE = BattleType.PUZZLE_ROYALE;

export default function useRoyaleSocket(): RoyaleSocketApi {
  const {send, ready, authed} = useFunChallengesSocket();

  const myUserId = useLoggedInUserStore(s => s.userId);
  const myUserIdFromUserData = useLoggedInUserStore(
    s => (s.userData && (s.userData.user_id ?? s.userData.pk)) || '',
  );
  const resolvedUserId = String(myUserIdFromUserData || myUserId || '');

  const createLobby = useCallback(
    (args: CreateRoyaleArgs) => {
      const settings: RoyaleSettings = {
        ...args.settings,
        hostIsPlayer: args.hostIsPlayer,
      };
      // Optimistically seed the lobby so the waiting room renders immediately
      // with the host's settings. fun_room_created confirms the real roomId.
      useFunChallengesRoyaleStore.getState().setLobby(
        {
          roomId: '',
          shareCode: '',
          hostId: resolvedUserId,
          hostIsPlayer: args.hostIsPlayer,
          state: 'waiting',
          players: [
            {
              userId: resolvedUserId,
              name: args.creatorName || '',
              avatarUrl: args.creatorAvatarUrl,
              isHost: true,
            },
          ],
          spectators: [],
          settings,
          // Server owns the authoritative expiry; this is a placeholder until
          // fun_room_created / a future room snapshot refines it.
          expiresAt:
            Date.now() + (Number(settings.lobbyExpiryMs) || 15 * 60 * 1000),
        },
        resolvedUserId,
      );

      send({
        type: 'fun_room_create',
        gameType: ROYALE,
        id: '',
        payload: {
          inviteeIds: [], // royale is created empty; players join by code/link
          creatorName: args.creatorName || '',
          creatorAvatarUrl: args.creatorAvatarUrl || '',
          hostIsPlayer: args.hostIsPlayer,
          settings,
        },
      });
    },
    [send, resolvedUserId],
  );

  const joinLobby = useCallback(
    (shareCodeOrRoomId: string, args: JoinRoyaleArgs = {}) => {
      const code = String(shareCodeOrRoomId || '').trim();
      // Optimistically seed a minimal lobby so the joiner navigates to the
      // waiting room IMMEDIATELY (the global listener fires on status==='lobby')
      // instead of being stuck on the options screen until the race starts.
      // The server only replies with fun_room_update (a roster), and our
      // routeRoyaleRoomUpdate is a no-op without an existing lobby — so without
      // this seed the joiner would never reach 'lobby'. We don't know the host
      // id / settings yet; fun_room_update backfills the roster and
      // fun_session_start backfills the rest. The share code is what the joiner
      // typed/clicked, so we can show it right away.
      const existing = useFunChallengesRoyaleStore.getState().lobby;
      if (!existing || existing.shareCode !== code) {
        useFunChallengesRoyaleStore.getState().setLobby(
          {
            roomId: code,
            shareCode: code,
            hostId: '',
            hostIsPlayer: true,
            state: 'waiting',
            players: [
              {
                userId: resolvedUserId,
                name: args.username || '',
                avatarUrl: args.avatarUrl,
                isHost: false,
              },
            ],
            spectators: [],
            settings: {} as RoyaleSettings,
            expiresAt: Date.now() + 15 * 60 * 1000,
          },
          resolvedUserId,
        );
      }
      // The id is a short share code OR a roomId; the server resolves either.
      // args.asSpectator is reserved for the PR-7 spectate path (server route
      // not built yet) — today every join is a participant join.
      send({
        type: 'fun_room_join',
        gameType: ROYALE,
        id: String(shareCodeOrRoomId || '').trim(),
        payload: {
          username: args.username || '',
          avatarUrl: args.avatarUrl || '',
        },
      });
    },
    [send, resolvedUserId],
  );

  const leaveLobby = useCallback(
    (roomId: string) => {
      send({
        type: 'fun_room_leave',
        gameType: ROYALE,
        id: String(roomId || ''),
        payload: {},
      });
    },
    [send],
  );

  const startRace = useCallback(
    (
      roomId: string,
      args: {hostIsPlayer?: boolean; hostWatching?: boolean} = {},
    ) => {
      const payload: {hostIsPlayer?: boolean; hostWatching?: boolean} = {};
      if (typeof args.hostIsPlayer === 'boolean') {
        payload.hostIsPlayer = args.hostIsPlayer;
      }
      // Classroom "coach watches": host is a wire-player (hostIsPlayer:true) but
      // a non-scoring watcher. The server registers them as a room spectator
      // (so they get the student's live board) and tells clients to hide their
      // row. Only sent when the coach chose Watch in a classroom.
      if (typeof args.hostWatching === 'boolean') {
        payload.hostWatching = args.hostWatching;
      }
      send({
        type: 'fun_room_start',
        gameType: ROYALE,
        id: String(roomId || ''),
        payload,
      });
    },
    [send],
  );

  const setHostRole = useCallback(
    (roomId: string, hostIsPlayer: boolean) => {
      send({
        type: 'fun_set_host_role',
        gameType: ROYALE,
        id: String(roomId || ''),
        payload: {hostIsPlayer},
      });
    },
    [send],
  );

  const probeSession = useCallback(() => {
    send({
      type: 'fun_session_rejoin',
      gameType: ROYALE,
      id: '',
      payload: {},
      // Same requestId the auto-rejoin probe uses, so the inbound error handler
      // suppresses the NO_ACTIVE_SESSION / SESSION_* replies (those just mean
      // "nothing to rejoin yet", which is the normal pre-start state).
      requestId: 'fun-rejoin-probe',
    });
  }, [send]);

  const sendMove = useCallback(
    (sessionId: string, correct: 0 | 1, square: string) => {
      send({
        type: 'fun_move',
        gameType: ROYALE,
        id: String(sessionId || ''),
        payload: {correct, square},
      });
    },
    [send],
  );

  const forfeit = useCallback(
    (sessionId: string) => {
      send({
        type: 'fun_forfeit',
        gameType: ROYALE,
        id: String(sessionId || ''),
        payload: {},
      });
    },
    [send],
  );

  const rematch = useCallback(
    (endedSessionId: string) => {
      send({
        type: 'fun_rematch',
        gameType: ROYALE,
        id: String(endedSessionId || ''),
        payload: {},
      });
    },
    [send],
  );

  const spectate = useCallback(
    (shareCodeOrRoomId: string, args: JoinRoyaleArgs = {}) => {
      send({
        type: 'fun_room_spectate',
        gameType: ROYALE,
        id: String(shareCodeOrRoomId || '').trim(),
        payload: {
          username: args.username || '',
          avatarUrl: args.avatarUrl || '',
        },
      });
    },
    [send],
  );

  const spectateMove = useCallback(
    (
      sessionId: string,
      fen: string,
      from: string,
      to: string,
      orientation: 'white' | 'black',
      kind: 'setup' | 'player' | 'reply' | 'wrong',
    ) => {
      send({
        type: 'fun_spectate_move',
        gameType: ROYALE,
        id: String(sessionId || ''),
        payload: {fen, from, to, orientation, kind},
      });
    },
    [send],
  );

  return useMemo(
    () => ({
      ready,
      authed,
      createLobby,
      joinLobby,
      leaveLobby,
      startRace,
      probeSession,
      setHostRole,
      sendMove,
      forfeit,
      rematch,
      spectate,
      spectateMove,
    }),
    [
      ready,
      authed,
      createLobby,
      joinLobby,
      leaveLobby,
      startRace,
      probeSession,
      setHostRole,
      sendMove,
      forfeit,
      rematch,
      spectate,
      spectateMove,
    ],
  );
}
